import { useMemo, useState } from 'react';
import {
  classifyScheduleStatus,
  scheduleFailureReason,
  scheduleNeedsAttention,
} from '@canopy/shared';
import {
  useAllSchedules,
  useDeleteSchedule,
  useRunScheduleNow,
  useScheduleDeliveries,
  useToggleSchedule,
  type Schedule,
} from '../../api/schedules.js';
import { useReportTemplates } from '../../api/report-templates.js';
import { useDashboards } from '../../api/dashboards.js';

/**
 * Admin → Schedules
 *
 * Tenant-wide view of every scheduled run. Groups schedules by the
 * artifact they target (template or dashboard), resolves artifact
 * names, surfaces next + last run, and exposes per-schedule actions
 * (enable/disable, run now, open recent deliveries, delete).
 */
export function SchedulesAdminPage(): JSX.Element {
  const { data: schedules, isLoading, error } = useAllSchedules();
  const { data: templates } = useReportTemplates();
  const { data: dashboards } = useDashboards();
  const toggle = useToggleSchedule();
  const run = useRunScheduleNow();
  const del = useDeleteSchedule();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled' | 'failing'>(
    'all',
  );

  const nameFor = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of templates ?? []) m.set(t.id, t.name);
    for (const d of dashboards ?? []) m.set(d.id, d.name);
    return m;
  }, [templates, dashboards]);

  const filtered = useMemo(() => {
    const list = schedules ?? [];
    switch (statusFilter) {
      case 'enabled':
        return list.filter((s) => s.enabled);
      case 'disabled':
        return list.filter((s) => !s.enabled);
      case 'failing':
        // CSAGE-346: classify via @canopy/shared, not raw string compares.
        return list.filter((s) => scheduleNeedsAttention(s.lastStatus));
      default:
        return list;
    }
  }, [schedules, statusFilter]);

  const stats = useMemo(() => {
    const list = schedules ?? [];
    return {
      total: list.length,
      enabled: list.filter((s) => s.enabled).length,
      failing: list.filter((s) => scheduleNeedsAttention(s.lastStatus)).length,
      nextIn: nextRunWithinHours(list, 1),
    };
  }, [schedules]);

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Schedules</h1>
          <p className="page-title-subtitle">
            Every tenant-wide scheduled run. Schedules live on templates or dashboards, but health
            is easier to read when they're centralized.
          </p>
        </div>
        <div className="page-title-actions">
          <span className="badge badge--neutral">{stats.total} total</span>
          <span className="badge badge--success">{stats.enabled} enabled</span>
          {stats.failing > 0 && (
            <span className="badge badge--danger">{stats.failing} failing</span>
          )}
        </div>
      </header>

      {stats.nextIn > 0 && (
        <div className="alert alert--info" style={{ marginBottom: 'var(--space-3)' }}>
          <strong>{stats.nextIn}</strong> schedule{stats.nextIn === 1 ? '' : 's'} will fire within
          the next hour.
        </div>
      )}

      <div className="row" style={{ gap: 8, marginBottom: 'var(--space-3)' }}>
        <FilterChip current={statusFilter} value="all" onClick={setStatusFilter}>
          All
        </FilterChip>
        <FilterChip current={statusFilter} value="enabled" onClick={setStatusFilter}>
          Enabled
        </FilterChip>
        <FilterChip current={statusFilter} value="disabled" onClick={setStatusFilter}>
          Disabled
        </FilterChip>
        <FilterChip current={statusFilter} value="failing" onClick={setStatusFilter}>
          Failing
        </FilterChip>
      </div>

      {isLoading && <div className="text-dim">Loading schedules…</div>}
      {error && <div className="alert alert--danger">Failed to load schedules.</div>}
      {schedules && filtered.length === 0 && !isLoading && (
        <div className="empty-state">
          <h3>No schedules</h3>
          <p>
            Schedules are created from a template or dashboard's Schedules tab. They'll appear here
            once someone sets one up.
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Artifact</th>
              <th>Cron</th>
              <th>Channels</th>
              <th>Next run</th>
              <th>Last run</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <ScheduleRow
                key={s.id}
                schedule={s}
                artifactName={nameFor.get(s.artifactId) ?? s.artifactId.slice(0, 8) + '…'}
                expanded={expanded === s.id}
                onExpand={() => setExpanded(expanded === s.id ? null : s.id)}
                onToggle={(enabled) => toggle.mutate({ id: s.id, enabled })}
                onRunNow={() => run.mutate(s.id)}
                onDelete={() => {
                  if (
                    !confirm(
                      `Delete schedule "${s.name}"? Running deliveries complete, but no future runs will fire.`,
                    )
                  )
                    return;
                  del.mutate(s.id);
                }}
                runBusy={run.isPending}
              />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function ScheduleRow({
  schedule,
  artifactName,
  expanded,
  onExpand,
  onToggle,
  onRunNow,
  onDelete,
  runBusy,
}: {
  schedule: Schedule;
  artifactName: string;
  expanded: boolean;
  onExpand(): void;
  onToggle(enabled: boolean): void;
  onRunNow(): void;
  onDelete(): void;
  runBusy: boolean;
}): JSX.Element {
  const s = schedule;
  const status = s.lastStatus;
  const channels = s.delivery.channels.map((c) => c.kind).join(', ');
  return (
    <>
      <tr style={{ opacity: s.enabled ? 1 : 0.6 }}>
        <td>
          <button type="button" className="sched-row-expand" onClick={onExpand}>
            <span
              className={'sched-row-caret' + (expanded ? ' sched-row-caret--open' : '')}
              aria-hidden="true"
            >
              ▸
            </span>
            <strong>{s.name}</strong>
          </button>
        </td>
        <td>
          <span className="text-xs text-dim">{s.artifactType.replace('_', ' ')} · </span>
          <code className="text-xs">{artifactName}</code>
        </td>
        <td>
          <code className="text-xs">{s.cron}</code>
          <span className="text-xs text-dim"> · {s.tz}</span>
        </td>
        <td className="text-xs">{channels}</td>
        <td className="text-xs text-dim">{formatDate(s.nextRunAt)}</td>
        <td className="text-xs text-dim">{formatDate(s.lastRunAt)}</td>
        <td>
          <StatusBadge status={status} enabled={s.enabled} />
        </td>
        <td className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onToggle(!s.enabled)}
          >
            {s.enabled ? 'Disable' : 'Enable'}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onRunNow}
            disabled={runBusy}
          >
            Run now
          </button>
          <button type="button" className="btn-icon" onClick={onDelete} aria-label="Delete">
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 5h10M6 5V3h4v2M5 5l1 9h4l1-9" />
            </svg>
          </button>
        </td>
      </tr>
      {expanded && <DeliveriesRow scheduleId={s.id} />}
    </>
  );
}

function DeliveriesRow({ scheduleId }: { scheduleId: string }): JSX.Element {
  const { data: deliveries, isLoading } = useScheduleDeliveries(scheduleId);
  return (
    <tr className="sched-deliveries-row">
      <td colSpan={8}>
        <div className="sched-deliveries">
          <h4>Recent deliveries</h4>
          {isLoading && <div className="text-dim text-sm">Loading…</div>}
          {deliveries && deliveries.length === 0 && (
            <div className="text-dim text-sm">No deliveries recorded yet.</div>
          )}
          {deliveries && deliveries.length > 0 && (
            <table className="table table--inner">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Delivered</th>
                  <th>Last error</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.slice(0, 10).map((d) => (
                  <tr key={d.id}>
                    <td className="text-xs">{formatDate(d.createdAt)}</td>
                    <td className="text-xs">{d.channel}</td>
                    <td>
                      <DeliveryStatusBadge status={d.status} />
                    </td>
                    <td className="text-xs">{d.attempts}</td>
                    <td className="text-xs text-dim">{formatDate(d.deliveredAt)}</td>
                    <td className="text-xs" style={{ color: 'var(--color-danger)' }}>
                      {d.lastError ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </td>
    </tr>
  );
}

function FilterChip({
  current,
  value,
  onClick,
  children,
}: {
  current: string;
  value: 'all' | 'enabled' | 'disabled' | 'failing';
  onClick(v: 'all' | 'enabled' | 'disabled' | 'failing'): void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      className={'chip' + (current === value ? ' chip--on' : '')}
      onClick={() => onClick(value)}
    >
      {children}
    </button>
  );
}

function StatusBadge({
  status,
  enabled,
}: {
  status: string | null;
  enabled: boolean;
}): JSX.Element {
  if (!enabled) return <span className="badge badge--neutral">paused</span>;
  const state = classifyScheduleStatus(status);
  if (state === 'never') return <span className="badge badge--neutral">idle</span>;
  if (state === 'succeeded') return <span className="badge badge--success">success</span>;
  if (state === 'running') return <span className="badge badge--accent">running</span>;
  if (state === 'partial') return <span className="badge badge--warning">partial</span>;
  const reason = scheduleFailureReason(status);
  return (
    <span className="badge badge--danger" title={reason ?? status ?? ''}>
      {reason ?? 'failed'}
    </span>
  );
}

function DeliveryStatusBadge({ status }: { status: string }): JSX.Element {
  if (status === 'sent') return <span className="badge badge--success">sent</span>;
  if (status === 'pending') return <span className="badge badge--accent">pending</span>;
  if (status === 'failed') return <span className="badge badge--danger">failed</span>;
  if (status === 'dead') return <span className="badge badge--danger">dead</span>;
  return <span className="badge badge--neutral">{status}</span>;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function nextRunWithinHours(list: Schedule[], hours: number): number {
  const cutoff = Date.now() + hours * 60 * 60 * 1000;
  return list.filter((s) => {
    if (!s.enabled || !s.nextRunAt) return false;
    const t = new Date(s.nextRunAt).getTime();
    return Number.isFinite(t) && t <= cutoff;
  }).length;
}
